/**
 * The queue's date filter: URL params in, a window out.
 *
 * Arun: *"need a way to filer by date - default is last 7 days. paginate - this
 * will grow quickly"*. The growth is real, and so is a complication 8loop's call
 * logs do not have: **half of this table has not happened yet.** A callback due on
 * Thursday is the most important row on the screen, and a literal "last 7 days"
 * window — six days ago 00:00 through now — excludes it. Default that on and every
 * account looks finished.
 *
 * The fix is where the control appears, not a special case in the query. The tabs
 * already split the table by time domain, so the date filter is offered on the
 * four tabs that accumulate history and withheld from Due and In flight, where
 * every row is pending by definition and there is nothing to trim. That keeps the
 * window meaning exactly what it says on every tab that has one.
 *
 * Encoding, mirroring how 8loop's own list pages do it — the rule in one param and
 * the dates in two, so a pasted link survives:
 *
 *   (nothing)                 last 7 days, the default
 *   ?date=last30              a named preset
 *   ?date=all                 no window; the × on the pill lands here
 *   ?from=…&to=…              a hand-picked range, ISO instants
 */

import { DateTime } from "luxon";
import {
  dateRangePresetRange,
  type DateRangePresetKey,
  type DateRangeValue,
} from "@/components/ui/date-range-picker";

/** Tabs whose rows are history, and so grow without bound. */
const HISTORY_GROUPS = new Set(["called", "attention", "failed", "closed"]);

export const QUEUE_DATE_DEFAULT: DateRangePresetKey = "last7";

const PRESET_KEYS: readonly DateRangePresetKey[] = [
  "today",
  "yesterday",
  "thisWeek",
  "monthToDate",
  "last7",
  "last30",
];

/** Whether this tab gets a date filter at all. */
export function groupWindowsByDate(group: string): boolean {
  return HISTORY_GROUPS.has(group);
}

export type QueueDateSelection = {
  /** Marks the row in the preset menu; undefined for a custom range or all time. */
  presetKey: DateRangePresetKey | undefined;
  /** What the trigger summarises; undefined means nothing is applied. */
  value: DateRangeValue | undefined;
  /** What goes to the API. Null means that end is open. */
  from: string | null;
  to: string | null;
  /** False when the URL is asking for something other than the default. */
  isDefault: boolean;
};

const NONE: QueueDateSelection = {
  presetKey: undefined,
  value: undefined,
  from: null,
  to: null,
  isDefault: true,
};

export function resolveQueueDate(input: {
  group: string;
  /** `?date=` — a preset key, `all`, or absent. */
  preset: string | null;
  from: string | null;
  to: string | null;
  timezone: string;
}): QueueDateSelection {
  const { preset, from, to, timezone } = input;
  const asked = Boolean(preset || from || to);

  // No window on the pending tabs, whatever the URL says. The params are left
  // alone rather than stripped, so switching Called → Due → Called comes back to
  // the range you were looking at.
  if (!groupWindowsByDate(input.group)) return { ...NONE, isDefault: !asked };

  if (preset === "all") return { ...NONE, isDefault: false };

  // A hand-picked range wins over a stale preset key.
  if (from && to) {
    const f = DateTime.fromISO(from);
    const t = DateTime.fromISO(to);
    if (f.isValid && t.isValid) {
      return {
        presetKey: undefined,
        value: { from: f.toJSDate(), to: t.toJSDate() },
        from: f.toUTC().toISO(),
        to: t.toUTC().toISO(),
        isDefault: false,
      };
    }
  }

  const key = PRESET_KEYS.find((k) => k === preset) ?? QUEUE_DATE_DEFAULT;
  const range = dateRangePresetRange(key, timezone);
  return {
    presetKey: key,
    value: range,
    from: range.from.toISOString(),
    to: range.to.toISOString(),
    isDefault: key === QUEUE_DATE_DEFAULT && !from && !to,
  };
}
